import React, { useContext, useMemo, useEffect } from "react";
import { setWithExpiry } from "shared/utils/localStorageAccess";
import {
  BuildDecomposedOptionRows,
  BuildFlatOptionRows,
  GetParentCompanyRpidsForGroup,
  ReconcileSelectedRpids,
  IsGroup,
} from "shared/helpers/OrganizationDecomposition";

import { ORGANIZATION_SELECTION_KEY } from "shared/helpers/UserSelectionCache";
import { UserContext } from "shared/context/UserContext";
import DecomposeGroupsToggle from "./DecomposeGroupsToggle";
import OrganizationDropdown from "./OrganizationDropdown/OrganizationDropdown";

const SELECTION_TTL_MS = 3600000;

export function Organization(props) {
  const userContext = useContext(UserContext);

  const currentOrganization = userContext.currentOrganization;
  const isDecomposed = userContext.isDecomposeGroupsEnabled;

  // Building the decomposed rows generates new child objects, so this is memoized to keep their
  // identity stable across renders - the dropdown uses them as React children and rebuilding them
  // every render would reset its filter state.
  const optionRows = useMemo(() => {
    return isDecomposed
      ? BuildDecomposedOptionRows(userContext.organizations)
      : BuildFlatOptionRows(userContext.organizations);
  }, [userContext.organizations, isDecomposed]);

  // Only meaningful for a GCT user who has the option on and a Group selected. Anything else
  // means "the whole organization", which is represented by an empty list.
  const isRefinable = isDecomposed && IsGroup(currentOrganization);

  const selectedRpids = useMemo(() => {
    return isRefinable ? userContext.selectedRpidsForGroup : [];
  }, [isRefinable, userContext.selectedRpidsForGroup]);

  // The option outlives the selection it refines - the flag is stored without an expiry while the
  // organization and its RPIDs expire after an hour - so the two can load out of step. Narrowing
  // the remembered RPIDs to the Group actually selected keeps the dropdown consistent with the
  // list it is showing, and falls back to the whole Group when nothing survives.
  useEffect(() => {
    if (!isRefinable) return;
    if (!userContext.organizations) return;

    const reconciledRpids = ReconcileSelectedRpids(
      currentOrganization,
      userContext.selectedRpidsForGroup
    );

    const isUnchanged =
      reconciledRpids.length === userContext.selectedRpidsForGroup.length &&
      reconciledRpids.every((rpid) =>
        userContext.selectedRpidsForGroup.includes(rpid)
      );

    if (isUnchanged) return;

    userContext.onSelectedRpidsForGroupChange(reconciledRpids);
  }, [
    isRefinable,
    currentOrganization,
    userContext.organizations,
    userContext.selectedRpidsForGroup,
  ]);

  function getContent() {
    if (!currentOrganization) return null;

    // While a Group is narrowed, the header reports how much of it is in play so the user is not
    // left looking at a Group label while seeing a subset of its data.
    const availableCount = isRefinable
      ? GetParentCompanyRpidsForGroup(currentOrganization).length
      : 0;

    const isNarrowed = isRefinable && selectedRpids.length < availableCount;

    return (
      <React.Fragment>
        <i className="fa fa-building" />
        {currentOrganization.label}
        {isNarrowed && (
          <span className="Organization--additional-count">
            {" (" + selectedRpids.length + " of " + availableCount + ")"}
          </span>
        )}
      </React.Fragment>
    );
  }

  const content = getContent();

  // Selecting a different organization discards any narrowing, because RPIDs only have meaning
  // against the Group they were chosen from. A newly selected Group starts as its whole self,
  // unless the caller narrowed it in the same action - clicking straight into one of its parent
  // companies. The remembered NCPDP selection is discarded for the same reason: those member
  // pharmacies belong to the organization they were picked from.
  function onOrganizationChange(organization, rpidsOverride) {
    if (!organization || !organization.value) return;

    //set the localstorage value for the selected organization - it will expire in 1 hour
    setWithExpiry(ORGANIZATION_SELECTION_KEY, organization, SELECTION_TTL_MS);
    userContext.onCurrentOrganizationChange(organization);
    userContext.onSelectedLocationMmidsChange([]);

    if (!isDecomposed || !IsGroup(organization)) {
      userContext.onSelectedRpidsForGroupChange([]);
      return;
    }

    userContext.onSelectedRpidsForGroupChange(
      Array.isArray(rpidsOverride) && rpidsOverride.length > 0
        ? rpidsOverride
        : GetParentCompanyRpidsForGroup(organization)
    );
  }

  function getOrganizationName() {
    if (userContext.organizations && currentOrganization) {
      //NOTE: We shouldn't need to see what the userType (as defined from Okta) is,
      //we should just base this on the email address/permissions from Admin itself
      return (
        <div className="Organization--header">
          <OrganizationDropdown
            optionRows={optionRows}
            onChange={onOrganizationChange}
            selectedOrganization={currentOrganization}
            selectedRpids={selectedRpids}
            onSelectedRpidsChange={userContext.onSelectedRpidsForGroupChange}
            isDecomposed={isDecomposed}
          >
            {content}
          </OrganizationDropdown>
          <DecomposeGroupsToggle />
        </div>
      );
    } else return null;
  }

  return getOrganizationName();
}
